// src/lib/motion/useInView.ts
// Whether an element is on screen, via IntersectionObserver. Without IO the
// element counts as in view, so content never stays hidden or paused-off.

import { useEffect, useState } from 'react'
import type { RefObject } from 'react'

export interface UseInViewOptions {
  /** Passed to IntersectionObserver, e.g. '100% 0px' to fire one viewport early. */
  rootMargin?: string
  /** Passed to IntersectionObserver. */
  threshold?: number
  /** Latch true after the first entry and stop observing. */
  once?: boolean
}

const hasIntersectionObserver = () => typeof IntersectionObserver === 'function'

export function useInView<T extends Element>(
  ref: RefObject<T | null>,
  { rootMargin, threshold, once = false }: UseInViewOptions = {},
): boolean {
  const [inView, setInView] = useState(() => !hasIntersectionObserver())

  useEffect(() => {
    const el = ref.current
    if (!el || !hasIntersectionObserver()) return

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1]
        if (!entry) return
        if (once) {
          if (!entry.isIntersecting) return
          setInView(true)
          observer.disconnect()
          return
        }
        setInView(entry.isIntersecting)
      },
      { rootMargin, threshold },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref, rootMargin, threshold, once])

  return inView
}
